import { type PermissionValue } from '@/types';
import { Ability, AbilityBuilder } from '@casl/ability';
import { atom } from 'jotai';

// Define actions
export const ACTIONS = {
  ACCESS: 'access',
} as const;

// Define all permission subjects based on BE permissions
export const PERMISSION_SUBJECTS = {
  // Billing related
  BILLING_CREATE: 'permission:billing:create',
  BILLING_DELETE: 'permission:billing:delete',
  BILLING_VIEW: 'permission:billing:view',
  BILLING_EDIT: 'permission:billing:edit',

  // Tier related
  TIER_CREATE: 'permission:tier:create',
  TIER_DELETE: 'permission:tier:delete',
  TIER_VIEW: 'permission:tier:view',
  TIER_EDIT: 'permission:tier:edit',

  // Account related
  ACCOUNT_CREATE: 'permission:account:create',
  ACCOUNT_DELETE: 'permission:account:delete',
  ACCOUNT_VIEW: 'permission:account:view',
  ACCOUNT_EDIT: 'permission:account:edit',

  // Role related
  ROLE_CREATE: 'permission:role:create',
  ROLE_DELETE: 'permission:role:delete',
  ROLE_VIEW: 'permission:role:view',
  ROLE_EDIT: 'permission:role:edit',
  ROLE_MEMBER_CREATE: 'permission:role:member:create',
  ROLE_MEMBER_DELETE: 'permission:role:member:delete',
  ROLE_MEMBER_VIEW: 'permission:role:member:view',
  ROLE_MEMBER_EDIT: 'permission:role:member:edit',

  // Team sharing related
  TEAM_SHARING_CREATE: 'permission:team_sharing:create',
  TEAM_SHARING_DELETE: 'permission:team_sharing:delete',
  TEAM_SHARING_VIEW: 'permission:team_sharing:view',
  TEAM_SHARING_EDIT: 'permission:team_sharing:edit',

  // Team related
  TEAM_CREATE: 'permission:team:create',
  TEAM_DELETE: 'permission:team:delete',
  TEAM_VIEW: 'permission:team:view',
  TEAM_EDIT: 'permission:team:edit',

  // Team template related
  TEAM_TEMPLATE_CREATE: 'permission:team_template:create',
  TEAM_TEMPLATE_DELETE: 'permission:team_template:delete',
  TEAM_TEMPLATE_VIEW: 'permission:team_template:view',
  TEAM_TEMPLATE_EDIT: 'permission:team_template:edit',

  // Template sharing (controls broad distribution: org-wide + specific-teams)
  TEMPLATE_SHARING_MANAGE: 'permission:template_sharing:manage',

  // Session related
  SESSION_CREATE: 'permission:session:create',
  SESSION_DELETE: 'permission:session:delete',
  SESSION_VIEW: 'permission:session:view',
  SESSION_EDIT: 'permission:session:edit',
  SESSION_SHARE: 'permission:session:share',

  // Transcription related
  TRANSCRIPTION_CREATE: 'permission:transcription:create',
  TRANSCRIPTION_DELETE: 'permission:transcription:delete',
  TRANSCRIPTION_VIEW: 'permission:transcription:view',
  TRANSCRIPTION_EDIT: 'permission:transcription:edit',

  // Document related
  DOCUMENT_CREATE: 'permission:document:create',
  DOCUMENT_DELETE: 'permission:document:delete',
  DOCUMENT_VIEW: 'permission:document:view',
  DOCUMENT_EDIT: 'permission:document:edit',

  // Notes related
  NOTES_CREATE: 'permission:notes:create',
  NOTES_DELETE: 'permission:notes:delete',
  NOTES_VIEW: 'permission:notes:view',
  NOTES_EDIT: 'permission:notes:edit',

  // Templates related
  TEMPLATES_CREATE: 'permission:templates:create',
  TEMPLATES_DELETE: 'permission:templates:delete',
  TEMPLATES_VIEW: 'permission:templates:view',
  TEMPLATES_EDIT: 'permission:templates:edit',

  // Ask AI related
  ASK_AI_CREATE: 'permission:ask_ai:create',
  ASK_AI_DELETE: 'permission:ask_ai:delete',
  ASK_AI_VIEW: 'permission:ask_ai:view',
  ASK_AI_EDIT: 'permission:ask_ai:edit',

  // Recording related
  RECORDING_VIEW: 'permission:recording:view',

  // Team others session related
  TEAM_OTHERS_SESSION_CREATE: 'permission:team_others_session:create',
  TEAM_OTHERS_SESSION_DELETE: 'permission:team_others_session:delete',
  TEAM_OTHERS_SESSION_VIEW: 'permission:team_others_session:view',
  TEAM_OTHERS_SESSION_EDIT: 'permission:team_others_session:edit',

  // Team others document related
  TEAM_OTHERS_DOC_CREATE: 'permission:team_others_doc:create',
  TEAM_OTHERS_DOC_DELETE: 'permission:team_others_doc:delete',
  TEAM_OTHERS_DOC_VIEW: 'permission:team_others_doc:view',
  TEAM_OTHERS_DOC_EDIT: 'permission:team_others_doc:edit',
  TEAM_OTHERS_DOC_SHARE: 'permission:team_others_doc:share',

  // Team others notes related
  TEAM_OTHERS_NOTES_CREATE: 'permission:team_others_notes:create',
  TEAM_OTHERS_NOTES_DELETE: 'permission:team_others_notes:delete',
  TEAM_OTHERS_NOTES_VIEW: 'permission:team_others_notes:view',
  TEAM_OTHERS_NOTES_EDIT: 'permission:team_others_notes:edit',
  TEAM_OTHERS_NOTES_SHARE: 'permission:team_others_notes:share',

  // Team others transcription related
  TEAM_OTHERS_TRANSCRIPTION_CREATE: 'permission:team_others_transcription:create',
  TEAM_OTHERS_TRANSCRIPTION_DELETE: 'permission:team_others_transcription:delete',
  TEAM_OTHERS_TRANSCRIPTION_VIEW: 'permission:team_others_transcription:view',
  TEAM_OTHERS_TRANSCRIPTION_EDIT: 'permission:team_others_transcription:edit',

  // Team analytics related (used for both team and org analytics dashboards)
  TEAM_ANALYTICS_VIEW: 'permission:team_analytics:view',

  // Patient profile related
  PATIENT_PROFILE_CREATE: 'permission:patient_profile:create',
  PATIENT_PROFILE_DELETE: 'permission:patient_profile:delete',
  PATIENT_PROFILE_VIEW: 'permission:patient_profile:view',
  PATIENT_PROFILE_EDIT: 'permission:patient_profile:edit',
} as const;

export const hasUpdatedAbilityAtom = atom<boolean>(false);

// Extract types
export type Actions = (typeof ACTIONS)[keyof typeof ACTIONS];
export type PermissionSubjects =
  (typeof PERMISSION_SUBJECTS)[keyof typeof PERMISSION_SUBJECTS];

export type AppAbility = Ability<[Actions, PermissionSubjects]>;

interface Permissions {
  permissions: PermissionValue[];
}

export function defineRulesFor(
  permissions?: Permissions,
  accessAllPermissions?: boolean
) {
  const { can, rules } = new AbilityBuilder<AppAbility>(Ability);

  if (accessAllPermissions) {
    Object.values(PERMISSION_SUBJECTS).forEach((subject) => {
      can(ACTIONS.ACCESS, subject);
    });
    // logger.debug('assistant permissions all permissions');
    return rules;
  }

  if (permissions?.permissions) {
    permissions.permissions.forEach((permission) => {
      if (
        permission.code &&
        Object.values(PERMISSION_SUBJECTS).includes(permission.code as PermissionSubjects)
      ) {
        can(ACTIONS.ACCESS, permission.code as PermissionSubjects);
      }
    });
    // logger.debug('assistant permissions', permissions);
  }
  return rules;
}

export const ability = new Ability<[Actions, PermissionSubjects]>(defineRulesFor());

export function updateAbility(permissions: Permissions) {
  ability.update(defineRulesFor(permissions));
}

export function accessAllPermissions() {
  ability.update(defineRulesFor(undefined, true));
}
